import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Lock, Phone, MapPin, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

function formatError(detail) {
  if (!detail) return 'Erreur inconnue';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', address: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Mot de passe trop court (min 6 caractères)');
      return;
    }
    setSubmitting(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
        address: form.address || null,
      });
      toast.success('Compte créé avec succès');
      navigate('/mon-compte');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Échec de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 bg-muted/30 py-12">
      <div className="w-full max-w-md bg-white border border-border rounded-2xl p-8 shadow-xl" data-testid="register-card">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-4">
            <UserPlus className="w-6 h-6 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-secondary mb-2">Créer un compte</h1>
          <p className="text-sm text-muted-foreground">Suivez vos commandes et devis</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              required
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Nom complet *"
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="register-name-input"
            />
          </div>

          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="Email *"
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="register-email-input"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              placeholder="Mot de passe (min. 6) *"
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="register-password-input"
            />
          </div>

          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              placeholder="Téléphone"
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="register-phone-input"
            />
          </div>

          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="Adresse"
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="register-address-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 py-3 rounded-lg font-medium transition-all disabled:opacity-50"
            data-testid="register-submit-btn"
          >
            {submitting ? 'Création…' : 'Créer mon compte'}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Déjà inscrit ?{' '}
          <Link to="/connexion" className="text-primary font-medium hover:underline" data-testid="link-to-login">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
};
